import { z } from "zod";
import { tradeCodeSchema } from "./catalog.js";

export const canonicalProjectTypeSchema = z.enum([...tradeCodeSchema.options, "bathroom_full_gut"]);
export type CanonicalProjectType = z.infer<typeof canonicalProjectTypeSchema>;

export const intakeClassificationCandidateSchema = z.object({
  projectType: canonicalProjectTypeSchema,
  confidence: z.number().min(0).max(1),
  evidenceStatements: z.array(z.string().min(1).max(500)).max(20),
});

export const projectClassificationSchema = z.object({
  classifications: z.array(
    z.object({
      workItemId: z.uuid(),
      candidates: z.array(intakeClassificationCandidateSchema).min(1).max(5),
      selectedProjectType: canonicalProjectTypeSchema.optional(),
      status: z.enum(["proposed", "clarification_required", "confirmed", "corrected"]),
    }),
  ),
  unsupportedRequests: z.array(z.string().min(1).max(500)).max(20),
  clarificationQuestion: z.string().min(1).max(500).optional(),
  classifierVersion: z.string().min(1).max(100),
  model: z
    .object({
      provider: z.string().min(1).max(60),
      model: z.string().min(1).max(120),
      promptVersion: z.string().min(1).max(100),
    })
    .optional(),
});
export type ProjectClassification = z.infer<typeof projectClassificationSchema>;

export const factOriginSchema = z.enum([
  "customer_statement",
  "manual_measurement",
  "printable_marker",
  "visual_observation",
  "industry_standard_approximation",
  "deterministic_derivation",
  "contractor_review",
  "contractor_site_measurement",
]);
export type FactOrigin = z.infer<typeof factOriginSchema>;

export const factStatusSchema = z.enum([
  "proposed",
  "accepted_for_planning",
  "accepted_for_provisional_quote",
  "rejected",
  "conflicted",
  "contractor_verified",
  "site_verified",
  "superseded",
]);

export const projectFactSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  workItemId: z.uuid().nullable().optional(),
  entityId: z.uuid().nullable().optional(),
  requirementId: z.string().min(1).max(160),
  field: z.string().min(1).max(160),
  rawValue: z.unknown(),
  normalizedValue: z.unknown().optional(),
  unit: z.string().max(40).optional(),
  origin: factOriginSchema,
  evidenceIds: z.array(z.uuid()),
  semanticConfidence: z.number().min(0).max(1).optional(),
  measurementUncertainty: z.number().nonnegative().optional(),
  status: factStatusSchema,
  customerConfirmed: z.boolean(),
  contractorConfirmed: z.boolean(),
  assumption: z.boolean(),
  supersedesId: z.uuid().nullable().optional(),
});
export type ProjectFact = z.infer<typeof projectFactSchema>;

export const measurementSourceSchema = z.enum([
  "customer_reported",
  "manual_tape",
  "printable_marker",
  "roomplan",
  "arcore_depth",
  "contractor_site_measurement",
]);

export const projectMeasurementRecordSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  entityId: z.uuid(),
  measurementType: z.string().min(1).max(160),
  rawValue: z.number().positive(),
  rawUnit: z.string().min(1).max(40),
  normalizedValue: z.number().positive().optional(),
  normalizedUnit: z.string().min(1).max(40).optional(),
  source: measurementSourceSchema,
  status: z.enum([
    "proposed",
    "provisional",
    "customer_confirmed",
    "contractor_verified",
    "site_verified",
    "rejected",
    "superseded",
  ]),
  confidence: z.number().min(0).max(1).optional(),
  uncertainty: z.number().nonnegative().optional(),
  captureIds: z.array(z.uuid()),
  normalization: z.record(z.string(), z.unknown()).optional(),
  validation: z.object({
    plausibleRange: z.boolean(),
    scaleVerified: z.boolean().optional(),
    samePlaneConfirmed: z.boolean().optional(),
    targetFullyVisible: z.boolean().optional(),
    crossCaptureDifferencePercent: z.number().nonnegative().optional(),
    matchesRecognizedStandard: z.boolean().optional(),
    warnings: z.array(z.string()),
  }),
  customerConfirmed: z.boolean(),
  contractorConfirmed: z.boolean(),
});
export type ProjectMeasurementRecord = z.infer<typeof projectMeasurementRecordSchema>;

export const projectReadinessSchema = z.object({
  projectId: z.uuid(),
  stateVersion: z.number().int().nonnegative().default(0),
  hardBlockers: z
    .array(
      z.object({
        id: z.string(),
        code: z.string(),
        message: z.string(),
        workItemId: z.uuid().optional(),
        requirementId: z.string().optional(),
      }),
    )
    .default([]),
  accuracyGaps: z
    .array(
      z.object({
        id: z.string(),
        message: z.string(),
        impact: z.string(),
        requirementId: z.string().optional(),
        resolution: z.enum(["unknown", "skipped", "default_applied", "omitted"]),
      }),
    )
    .default([]),
  nextAction: z
    .object({
      actionId: z.string(),
      question: z.string(),
      choices: z.array(z.object({ value: z.string(), label: z.string() })).default([]),
      skipEligible: z.boolean(),
    })
    .nullable()
    .default(null),
  planningEstimateReady: z.boolean(),
  provisionalEstimateReady: z.boolean(),
  contractorReviewReady: z.boolean(),
  missingRequirements: z.array(z.string()),
  conflictedRequirements: z.array(z.string()),
  assumptionsRequiringConfirmation: z.array(z.string()),
  measurementsRequiringConfirmation: z.array(z.string()),
  warnings: z.array(z.string()),
  evaluatedAgainst: z.object({
    requirementSchemaVersion: z.string(),
    playbookVersion: z.string().optional(),
    pricebookVersion: z.string().optional(),
  }),
});
export type ProjectReadiness = z.infer<typeof projectReadinessSchema>;

export const projectRequirementDefinitionSchema = z.object({
  id: z.string().min(1).max(160),
  projectType: canonicalProjectTypeSchema,
  entityType: z.string().min(1).max(80).optional(),
  field: z.string().min(1).max(160),
  valueType: z.enum(["string", "boolean", "number", "measurement", "enum", "photo", "count"]),
  requiredFor: z.array(
    z.enum(["planning_estimate", "provisional_estimate", "contractor_review", "final_quote"]),
  ),
  acceptedSources: z.array(factOriginSchema),
  quoteImpact: z.enum(["low", "medium", "high"]),
  customerConfirmationRequired: z.boolean(),
  contractorConfirmationRequired: z.boolean(),
  assumptionPolicy: z.enum(["not_allowed", "allowed_with_confirmation", "allowed_for_planning"]),
  validationRule: z.string().optional(),
  normalizationRule: z.string().optional(),
  clarificationPrompt: z.string().optional(),
  captureInstruction: z.string().optional(),
  conditionalOn: z.unknown().optional(),
});
export type ProjectRequirementDefinition = z.infer<typeof projectRequirementDefinitionSchema>;
